export const BLEED = 12;

// --- gunmetal / cyan palette ---
const CHARCOAL = '#2a2f38';   // outer charcoal stroke
const STEEL = '#5a6675';      // steel highlight line (low opacity)
const TEAL = '#35d0e0';       // cyan accent
const TEAL_HI = '#7fe9f2';
const TEAL_LO = '#1fb6c8';
const BODY = '#13161b';       // gunmetal body fill
const BODY_HI = '#1c2128';    // raised panel
const LCD_BG = '#04181c';     // dark LCD glass
const PURPLE = '#7c6ff7';     // faint corner sheen
const LABEL = '#9fb6bd';      // dim chrome label text

// A tiny faceplate button: rounded rect with a centred label.
function ChipBtn({ x, y, w, h, label, fs = 5, fill = BODY_HI }) {
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} rx={2} ry={2} fill={fill} opacity={0.96} />
      <rect x={x} y={y} width={w} height={h} rx={2} ry={2} fill="none" stroke={TEAL} strokeWidth={0.6} opacity={0.5} />
      <text x={x + w / 2} y={y + h / 2 + fs * 0.36} fill={TEAL_HI} fontSize={fs} fontWeight="800" textAnchor="middle">{label}</text>
    </g>
  );
}

// A dark gunmetal multi-function car deck ("FM 103.7"). All head-unit chrome —
// LCD strip + EQ cluster + labelled buttons on the TOP band, a transport row +
// mini readout on the BOTTOM band, a numbered preset rail on the RIGHT edge and
// two tiny buttons on the LEFT edge — lives in the bleed / edge padding so the
// card's title, date, location and Going button stay clear.
export default function BlackDeckFrame({ w, h, radius = 12, accent = TEAL }) {
  const W = w + BLEED * 2;
  const H = h + BLEED * 2;
  const x = BLEED;
  const y = BLEED;
  const r = radius + 1;

  // --- TOP LCD strip (sits across the top edge, inside the bleed) ---
  const stripPad = 30;            // leave room for SRC/BAND (left) + OPEN (right)
  const stripX = x + stripPad;
  const stripW = w - stripPad - 22;
  const stripH = 15;
  const stripY = y - 8;

  // EQ bar cluster — right side of the LCD.
  const eqHeights = [4, 8, 5, 9, 6, 3];
  const eqBarW = 1.8;
  const eqGap = 1.6;
  const eqCount = eqHeights.length;
  const eqTotalW = eqCount * eqBarW + (eqCount - 1) * eqGap;
  const eqX = stripX + stripW - 7 - eqTotalW;
  const eqBase = stripY + stripH - 3.5;

  // --- BOTTOM transport row (centred, below the footer) ---
  const tY = y + h + 1;           // transport band baseline
  const tBtnH = 8;
  const tBtnW = 13;
  const tGap = 5;
  const tCount = 3;
  const tTotalW = tCount * tBtnW + (tCount - 1) * tGap;
  const tX = x + (w - tTotalW) / 2;
  const gMid = tY + tBtnH / 2;

  // --- RIGHT preset rail (6 numbered buttons hugging the right edge) ---
  const railW = 12;
  // Sit the rail mostly in the right bleed (~8px intrusion, within the card's
  // 14px right padding) and stop the presets above the footer so they never
  // cover the Going / Interested badge.
  const railX = x + w - railW + 4;
  const presetN = 6;
  const railTop = y + 26;
  const railBot = y + h - 42;
  const railH = railBot - railTop;
  const pH = 9;
  const pStep = (railH - pH) / (presetN - 1);

  // --- LEFT rail (EQ / DSP) ---
  const lW = 14;
  // Pull the left rail into the bleed so the EQ / DSP buttons clear the title /
  // content column (which starts at the card's 14px left padding).
  const lX = x - 4;

  return (
    <div style={{ position: 'absolute', top: -BLEED, left: -BLEED, width: W, height: H, pointerEvents: 'none' }}>
      <svg width={W} height={H} xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="blackdeck_deckBody" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor={BODY_HI} stopOpacity="0.9" />
            <stop offset="1" stopColor={BODY} stopOpacity="0.95" />
          </linearGradient>
          <linearGradient id="blackdeck_deckSheen" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor={PURPLE} stopOpacity="0.22" />
            <stop offset="0.4" stopColor={PURPLE} stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* ---- BEZEL: gunmetal frame ---- */}
        {/* outer charcoal stroke */}
        <rect x={x - 3} y={y - 3} width={w + 6} height={h + 6} rx={r + 3} ry={r + 3} fill="none" stroke={CHARCOAL} strokeWidth={3.5} opacity={0.95} />
        {/* steel highlight line (low opacity) */}
        <rect x={x - 1} y={y - 1} width={w + 2} height={h + 2} rx={r + 1} ry={r + 1} fill="none" stroke={STEEL} strokeWidth={0.8} opacity={0.45} />
        {/* thin teal inner line (low opacity) */}
        <rect x={x} y={y} width={w} height={h} rx={r} ry={r} fill="none" stroke={accent} strokeWidth={1} opacity={0.4} />
        {/* faint purple sheen at top-left corner */}
        <rect x={x} y={y} width={w * 0.5} height={h * 0.4} rx={r} ry={r} fill="url(#blackdeck_deckSheen)" />

        {/* ===================== TOP BAND ===================== */}
        {/* rotary-knob hint near top-left corner (concentric circles) */}
        <g>
          <circle cx={x + 6} cy={y + 8} r={5.2} fill="url(#blackdeck_deckBody)" stroke={STEEL} strokeWidth={0.7} opacity={0.9} />
          <circle cx={x + 6} cy={y + 8} r={3} fill="none" stroke={accent} strokeWidth={0.7} opacity={0.6} />
          <circle cx={x + 6} cy={y + 8} r={1} fill={TEAL_HI} opacity={0.9} />
          <line x1={x + 6} y1={y + 8} x2={x + 6} y2={y + 4} stroke={TEAL_HI} strokeWidth={0.8} opacity={0.85} />
        </g>

        {/* SRC + BAND buttons at top-left (below the knob, in the top bleed) */}
        <ChipBtn x={x + 14} y={y - 8} w={15} h={7} label="SRC" />
        <ChipBtn x={x + 14} y={y + 1} w={15} h={6} label="BAND" fs={4.4} />

        {/* OPEN button at top-right */}
        <ChipBtn x={x + w - 21} y={y - 8} w={19} h={7} label="OPEN" />

        {/* Top LCD display strip */}
        <g>
          <rect x={stripX} y={stripY} width={stripW} height={stripH} rx={3.5} ry={3.5} fill={LCD_BG} opacity={0.97} />
          <rect x={stripX} y={stripY} width={stripW} height={stripH} rx={3.5} ry={3.5} fill="none" stroke={accent} strokeWidth={0.8} opacity={0.7} />
          {/* FM 103.7 readout */}
          <text x={stripX + 6} y={stripY + 10.5} fill={TEAL_HI} fontSize={7.5} fontWeight="800" letterSpacing={0.8}>FM</text>
          <text x={stripX + 21} y={stripY + 10.5} fill={accent} fontSize={7.5} fontWeight="800" letterSpacing={0.5}>103.7</text>
          {/* tiny status labels */}
          <text x={stripX + 50} y={stripY + 6} fill={LABEL} fontSize={4} fontWeight="800">ST</text>
          <text x={stripX + 50} y={stripY + 12} fill={LABEL} fontSize={4} fontWeight="800">DX</text>
          <text x={stripX + 61} y={stripY + 6} fill={TEAL_LO} fontSize={4} fontWeight="800">EQ</text>
          {/* EQ bar cluster — right side */}
          {eqHeights.map((bh, i) => (
            <rect
              key={i}
              x={eqX + i * (eqBarW + eqGap)}
              y={eqBase - bh}
              width={eqBarW}
              height={bh}
              rx={0.5}
              fill={i % 2 === 0 ? TEAL_HI : TEAL_LO}
              opacity={0.92}
            />
          ))}
        </g>

        {/* ===================== LEFT RAIL ===================== */}
        <ChipBtn x={lX} y={y + 34} w={lW} h={9} label="EQ" fs={4.6} />
        <ChipBtn x={lX} y={y + 46} w={lW} h={9} label="DSP" fs={4.4} />

        {/* ===================== RIGHT RAIL (presets 1..6) ===================== */}
        <g>
          {Array.from({ length: presetN }).map((_, i) => (
            <ChipBtn
              key={i}
              x={railX}
              y={railTop + i * pStep}
              w={railW}
              h={pH}
              label={String(i + 1)}
              fs={5}
            />
          ))}
        </g>

        {/* ===================== BOTTOM BAND ===================== */}
        {/* SCN button (bottom-left) */}
        <ChipBtn x={x + 4} y={tY} w={16} h={tBtnH} label="SCN" />
        {/* RPT button (bottom-right) */}
        <ChipBtn x={x + w - 20} y={tY} w={16} h={tBtnH} label="RPT" />

        {/* Transport row — rewind / play-pause / fast-forward as SVG glyphs */}
        <g>
          {/* rewind: two left triangles */}
          <rect x={tX} y={tY} width={tBtnW} height={tBtnH} rx={2} ry={2} fill={BODY_HI} stroke={TEAL} strokeWidth={0.6} strokeOpacity={0.5} opacity={0.96} />
          <path d={`M ${tX + 6.5} ${gMid - 2.4} L ${tX + 3.8} ${gMid} L ${tX + 6.5} ${gMid + 2.4} Z`} fill={TEAL_HI} />
          <path d={`M ${tX + 9.6} ${gMid - 2.4} L ${tX + 6.9} ${gMid} L ${tX + 9.6} ${gMid + 2.4} Z`} fill={TEAL_HI} />

          {/* play/pause: right triangle + two bars */}
          <rect x={tX + tBtnW + tGap} y={tY} width={tBtnW} height={tBtnH} rx={2} ry={2} fill={BODY_HI} stroke={TEAL} strokeWidth={0.6} strokeOpacity={0.5} opacity={0.96} />
          <path d={`M ${tX + tBtnW + tGap + 3} ${gMid - 2.6} L ${tX + tBtnW + tGap + 6.4} ${gMid} L ${tX + tBtnW + tGap + 3} ${gMid + 2.6} Z`} fill={TEAL_HI} />
          <rect x={tX + tBtnW + tGap + 7.6} y={gMid - 2.6} width={1.5} height={5.2} fill={TEAL_HI} />
          <rect x={tX + tBtnW + tGap + 9.7} y={gMid - 2.6} width={1.5} height={5.2} fill={TEAL_HI} />

          {/* fast-forward: two right triangles */}
          <rect x={tX + 2 * (tBtnW + tGap)} y={tY} width={tBtnW} height={tBtnH} rx={2} ry={2} fill={BODY_HI} stroke={TEAL} strokeWidth={0.6} strokeOpacity={0.5} opacity={0.96} />
          <path d={`M ${tX + 2 * (tBtnW + tGap) + 3.4} ${gMid - 2.4} L ${tX + 2 * (tBtnW + tGap) + 6.1} ${gMid} L ${tX + 2 * (tBtnW + tGap) + 3.4} ${gMid + 2.4} Z`} fill={TEAL_HI} />
          <path d={`M ${tX + 2 * (tBtnW + tGap) + 6.5} ${gMid - 2.4} L ${tX + 2 * (tBtnW + tGap) + 9.2} ${gMid} L ${tX + 2 * (tBtnW + tGap) + 6.5} ${gMid + 2.4} Z`} fill={TEAL_HI} />
        </g>

        {/* mini readout: DYNAMIC BASS BOOST (tiny cyan text under transport) */}
        <text x={x + w / 2} y={tY + tBtnH + 6} fill={TEAL} fontSize={4.6} fontWeight="800" letterSpacing={1} textAnchor="middle">DYNAMIC BASS BOOST</text>
      </svg>
    </div>
  );
}
